import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Trash2, Copy, Check, MessageSquareQuote, Eye, TrendingUp, Award } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMBED_CODE = `<div id="jh-reviews"></div>\n<script src="${BACKEND_URL}/api/widget.js" async></script>`;

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} className={i <= n ? "text-[#D4AF37]" : "text-zinc-700"} fill={i <= n ? "#D4AF37" : "none"} />
    ))}
  </span>
);

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-border bg-card rounded-lg p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest mb-3">
      <Icon size={14} className="text-[#D4AF37]" /> {label}
    </div>
    <div className="font-display text-4xl text-[#F5F5F0]">{value}</div>
  </div>
);

export default function Dashboard() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0, published: 0, five_star: 0 });
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [r, s] = await Promise.all([axios.get(`${API}/reviews`), axios.get(`${API}/stats`)]);
    setReviews(r.data);
    setStats(s.data);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await axios.delete(`${API}/reviews/${id}`);
    toast.success("Review deleted");
    load();
  };

  const copyEmbed = () => {
    navigator.clipboard.writeText(EMBED_CODE);
    setCopied(true);
    toast.success("Embed code copied — paste it into a Squarespace Code Block");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background px-6 sm:px-10 py-10 max-w-6xl mx-auto">
      <header className="mb-10">
        <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-2">Jeff Harrington</p>
        <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter text-[#F5F5F0]">Review Dashboard</h1>
        <p className="text-zinc-400 mt-2 text-sm">All student submissions — 4★ and above publish automatically.</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={MessageSquareQuote} label="Total Reviews" value={stats.total} testId="stat-total" />
        <StatCard icon={TrendingUp} label="Average Rating" value={stats.average} testId="stat-average" />
        <StatCard icon={Eye} label="Published" value={stats.published} testId="stat-published" />
        <StatCard icon={Award} label="5-Star" value={stats.five_star} testId="stat-five-star" />
      </div>

      <section className="border border-border bg-card rounded-lg p-6 mb-10" data-testid="embed-section">
        <h2 className="font-display text-lg md:text-lg text-[#F5F5F0] mb-2">Squarespace Embed Widget</h2>
        <p className="text-zinc-400 text-sm mb-4">
          On jeffharrington.com/reviews, add a <span className="text-[#E5C865]">Code Block</span> and paste this snippet.
          It automatically shows all published (4★+) reviews.
        </p>
        <div className="relative">
          <pre className="bg-black/50 border border-white/10 rounded-md p-4 text-xs text-zinc-300 overflow-x-auto" data-testid="embed-code">
            {EMBED_CODE}
          </pre>
          <button
            data-testid="copy-embed-btn"
            onClick={copyEmbed}
            className="absolute top-3 right-3 gold-btn px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-zinc-500 text-xs mt-3">
          Preview the public reviews wall at{" "}
          <a href="/reviews" className="text-[#E5C865] underline underline-offset-2" data-testid="reviews-wall-link">/reviews</a>
        </p>
      </section>

      <section className="border border-border bg-card rounded-lg overflow-hidden" data-testid="reviews-table-section">
        <div className="p-6 pb-0">
          <h2 className="font-display text-lg md:text-lg text-[#F5F5F0] mb-4">All Submissions</h2>
        </div>
        {reviews.length === 0 ? (
          <p className="text-zinc-500 text-sm p-6 pt-2" data-testid="no-reviews-msg">No reviews yet. Share the funnel link to start collecting.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-zinc-400">Name</TableHead>
                <TableHead className="text-zinc-400">Rating</TableHead>
                <TableHead className="text-zinc-400">Review</TableHead>
                <TableHead className="text-zinc-400">Date</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.map((r) => (
                <TableRow key={r.id} className="border-border hover:bg-white/[0.03]" data-testid={`review-row-${r.id}`}>
                  <TableCell className="text-[#F5F5F0] font-medium whitespace-nowrap">{r.name}</TableCell>
                  <TableCell><Stars n={r.rating} /></TableCell>
                  <TableCell className="text-zinc-400 text-xs max-w-[380px]">{r.feedback || r.experience || "—"}</TableCell>
                  <TableCell className="text-zinc-500 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <span className={`text-[11px] px-2 py-1 rounded-full border whitespace-nowrap ${
                      r.published
                        ? "border-[#D4AF37]/40 text-[#E5C865] bg-[#D4AF37]/10"
                        : "border-white/15 text-zinc-500"
                    }`}>
                      {r.published ? "Published" : "Private"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      data-testid={`delete-review-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="text-zinc-600 hover:text-red-400 transition-colors duration-200"
                      aria-label="Delete review"
                    >
                      <Trash2 size={15} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}
